import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux"; // Assuming Redux is used for authData
import { apiHandler } from "../../api"; // Adjust the path to your apiHandler utility
import { FaSpinner } from 'react-icons/fa'; // Icon for loading
import { Alert } from 'react-bootstrap'; // Assuming you have bootstrap components available
import { API_BASE_URL, endpoint } from "../../api/endpoint";
import { useNavigate } from "react-router-dom";


const Faq = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Fetch required auth state for apiHandler
  const { authData, storeId } = useSelector(state => state.loginSlice) || {};

  const [faqData, setFaqData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeAccordion, setActiveAccordion] = useState(null);

  const fetchFaqs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
       let url = `${endpoint.GET_FAQS}?type=rsf`; // Assuming the API endpoint accepts a type parameter to filter FAQs for RSF
      // The API endpoint does not require search/pagination params, so a simple GET request is sufficient.
      const result = await apiHandler({
        url:url,
        method: "GET",
        authToken: authData,
        dispatch,
        storeId,
      });

      if (result?.data?.code === 200 && Array.isArray(result.data.data)) {
        // Map the API data and assign an ID for local state management (like the accordion)
        const formattedFaq = result.data.data.map((item, index) => ({
          id: index,
          question: item.question,
          answer: item.answer,
        }));
        setFaqData(formattedFaq);
        // Set the first item as active by default, or keep null
        setActiveAccordion(formattedFaq.length > 0 ? formattedFaq[0].id : null); 
      } else {
        setError("Failed to fetch FAQs or received unexpected data structure.");
        setFaqData([]);
      }
    } catch (err) {
      
      setError(err.message || "An error occurred while fetching data.");
      setFaqData([]);
    } finally {
      setLoading(false);
    }
  }, [authData, dispatch, storeId]);

  useEffect(() => {
    // Ensure we have necessary auth credentials before fetching
    if (authData) {
      fetchFaqs();
    } else {
        setLoading(false);
        setError("Authentication data missing. Cannot fetch FAQs.");
    }
  }, [authData, fetchFaqs]);

  const toggleAccordion = (id) => {
    setActiveAccordion(activeAccordion === id ? null : id);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <FaSpinner size={30} className="spinner-border text-primary" />
          <p className="mt-2 text-muted">Loading FAQs...</p>
        </div>
      );
    }

    if (error) {
      return (
        <Alert variant="danger" className="mx-auto" style={{ maxWidth: "800px" }}>
          Error: {error}
        </Alert>
      );
    }

    if (faqData.length === 0) {
        return (
            <div className="text-center py-5 text-muted">
                No FAQs found.
            </div>
        );
    }

    return (
      <div className="accordion accordion-flush" id="faqAccordion">
        {faqData.map((faq) => (
          <div key={faq.id} className="accordion-item border-0 mb-3">
            <div className="card shadow-sm border-0 rounded-3">
              <h2 className="accordion-header">
                <button
                  className={`accordion-button fw-semibold py-3 px-3 border-0 rounded-3 ${
                    activeAccordion !== faq.id ? "collapsed" : ""
                  } ${
                    activeAccordion === faq.id
                      ? "bg-primary text-white shadow-none"
                      : "bg-light-grey"
                  }`}
                  type="button"
                  onClick={() => toggleAccordion(faq.id)}
                  style={{
                    fontSize: "1rem",
                    boxShadow: "none",
                    transition: "all 0.3s ease",
                  }}
                >
                  {faq.question}
                </button>
              </h2>
              <div
                className={`accordion-collapse collapse ${
                  activeAccordion === faq.id ? "show" : ""
                }`}
              >
                <div className="accordion-body px-4 py-4 bg-white rounded-bottom">
                  <p className="text-muted mb-0 lh-lg">{faq.answer}</p>
                  
                  {/* Since we don't know the exact nature of the links for the new data, 
                      I'm removing the conditional links (faq.id === 2) 
                      unless the new API provides structured link data. */}
                      
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="bg-light min-vh-100 py-5">
        <div className="row justify-content-center">
          <div className="col-10">
            {/* Header Section */}
            <div className="text-center mb-5">
              <p className="text-muted text-uppercase fw-semibold mb-2 letter-spacing-1">
                RSF COMMON FAQS
              </p>
              <h1 className="display-6 fw-bold text-dark mb-0">
                Frequently Asked Questions
              </h1>
            </div>

            {/* FAQ Accordion Content */}
            {renderContent()}

            {/* Help Button - Kept for visual continuity */}
            <div className="text-center mt-5">
              <button
                className="btn btn-primary btn-lg rounded-pill px-3 py-2 fw-semibold shadow-sm"
                style={{
                  border: "none",
                  transition: "all 0.3s ease",
                }}
                  onClick={() => navigate("/contact-us")}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow =
                    "0 8px 25px rgba(79, 70, 229, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "0 2px 10px rgba(0, 0, 0, 0.1)";
                }}
              >
                Get help
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Faq;